import type { KeyboardEvent } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { CARD_WIDTH, IDEA_KIND_ICON, IDEA_KIND_LABEL } from "@/lib/tilbudsarbeidsrom/idea-board";
import type { IdeaCardPatch } from "@/hooks/use-offer-idea-cards";
import type { DBOfferIdeaCard } from "@/lib/db-types";
import { IdeaCardBody } from "./IdeaCardBody";
import { usePointerDrag } from "./use-pointer-drag";

const NUDGE = 24;

interface IdeaCardProps {
  card: DBOfferIdeaCard;
  imageUrl: string | undefined;
  usedInBudget: boolean;
  /** Stablingsrekkefølge på tavlen; høyere ligger øverst. */
  stackOrder: number;
  readOnly: boolean;
  uploading: boolean;
  /** Ny posisjon etter dra eller piltast (relativ forskyvning). */
  onMove: (card: DBOfferIdeaCard, dx: number, dy: number) => void;
  onPatch: (patch: IdeaCardPatch) => void;
  onUpload: (file: File) => void;
  onDelete: () => void;
  onMakeBudgetLine: () => void;
}

/**
 * Ett kort på Idé-tavlen (VIB-2980). Dras i toppen, eller flyttes med
 * piltastene når toppen har fokus. Kortet er internt og vises aldri for kunden.
 */
export function IdeaCard({
  card, imageUrl, usedInBudget, stackOrder, readOnly, uploading, onMove, onPatch, onUpload, onDelete, onMakeBudgetLine,
}: IdeaCardProps) {
  const { offset, handlers } = usePointerDrag(({ dx, dy }) => onMove(card, dx, dy), readOnly);
  const label = IDEA_KIND_LABEL[card.kind];

  function onHandleKey(e: KeyboardEvent<HTMLDivElement>) {
    if (readOnly) return;
    const step = e.shiftKey ? NUDGE * 4 : NUDGE;
    const delta: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step],
    };
    const d = delta[e.key];
    if (!d) return;
    e.preventDefault();
    onMove(card, d[0], d[1]);
  }

  return (
    <article
      aria-label={`${label}${card.body ? `: ${card.body.slice(0, 60)}` : ""}`}
      className={cn(
        "absolute bg-card border-[1.5px] border-foreground/80 rounded-md shadow-sm flex flex-col",
        card.kind === "question" && "border-dashed",
        offset && "shadow-lg cursor-grabbing",
      )}
      style={{
        left: card.x + (offset?.dx ?? 0),
        top: card.y + (offset?.dy ?? 0),
        width: CARD_WIDTH,
        // Under dra ligger kortet over alt annet.
        zIndex: offset ? 10000 : stackOrder,
      }}
    >
      <div
        {...handlers}
        role="button"
        tabIndex={readOnly ? -1 : 0}
        aria-label={readOnly ? label : `Flytt ${label.toLowerCase()} (piltaster)`}
        aria-roledescription="flyttbart kort"
        onKeyDown={onHandleKey}
        className={cn(
          "flex items-center gap-1.5 px-2 py-1.5 border-b border-border text-[11px] uppercase tracking-wide font-medium text-muted-foreground select-none touch-none",
          !readOnly && "cursor-grab focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring",
        )}
      >
        <SrIcon name={IDEA_KIND_ICON[card.kind]} className="w-3.5 h-3.5" aria-hidden />
        <span>{label}</span>
        {usedInBudget && (
          <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0 normal-case tracking-normal">
            Budsjett
          </Badge>
        )}
        {!readOnly && (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onDelete}
            className={cn("p-0.5 rounded-sm hover:text-destructive", !usedInBudget && "ml-auto")}
            aria-label={`Slett ${label.toLowerCase()}`}
          >
            <SrIcon name="trash-2" className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="p-2">
        <IdeaCardBody
          card={card}
          imageUrl={imageUrl}
          readOnly={readOnly}
          uploading={uploading}
          onPatch={onPatch}
          onUpload={onUpload}
        />
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1.5 border-t border-border">
        <Checkbox
          id={`idea-ai-${card.id}`}
          checked={card.use_for_ai}
          disabled={readOnly}
          onCheckedChange={(v) => onPatch({ use_for_ai: v === true })}
        />
        <label htmlFor={`idea-ai-${card.id}`} className="text-[11px] text-muted-foreground cursor-pointer">
          Bruk som kilde for AI
        </label>
        {!readOnly && (
          <Button type="button" variant="ghost" size="sm" className="ml-auto h-7 px-2 text-xs" onClick={onMakeBudgetLine}>
            <SrIcon name="table" className="w-3.5 h-3.5" />
            Lag budsjettlinje
          </Button>
        )}
      </div>
    </article>
  );
}
